/**
 * Full Video Section Script
 * Uses visibility-based initialization and a poster-first YouTube embed
 * to avoid loading the YouTube iframe API on page load.
 */

(function () {
  'use strict';

  const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));
  const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube-nocookie.com';

  const SELECTORS = {
    section: 'full-video-section',
    container: '.video-container',
    video: 'video',
    youtubeWrapper: '.youtube-wrapper',
    youtubePlayer: '[data-youtube-player]',
    soundBtn: '.sound-btn',
    playPauseBtn: '.play-pause-overlay',
  };

  const CLASSES = {
    paused: 'is-paused',
    unmuted: 'unmuted',
    youtubeLoaded: 'youtube-wrapper--loaded',
  };

  function isElementInViewport(element) {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    return !(
      rect.bottom <= 0 ||
      rect.top >= window.innerHeight ||
      rect.right <= 0 ||
      rect.left >= window.innerWidth
    );
  }

  function postYouTubeCommand(iframe, func, args = []) {
    if (!iframe?.contentWindow) return;

    iframe.contentWindow.postMessage(JSON.stringify({
      event: 'command',
      func,
      args,
    }), YOUTUBE_EMBED_ORIGIN);
  }

  class VideoSection {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.type = container.dataset.videoSource;
      this.autoplay = container.dataset.autoplay === 'true';
      this.defaultMuted = container.dataset.muted !== 'false';
      this.loop = container.dataset.loop !== 'false';

      this.video = this.container.querySelector(SELECTORS.video);
      this.youtubeWrapper = this.container.querySelector(SELECTORS.youtubeWrapper);
      this.youtubePlayerMount = this.container.querySelector(SELECTORS.youtubePlayer);
      this.soundBtn = this.container.querySelector(SELECTORS.soundBtn);
      this.playPauseBtn = this.container.querySelector(SELECTORS.playPauseBtn);

      this.youtubeVideoId = this.youtubeWrapper?.dataset.videoId || '';
      this.youtubeTitle = this.youtubeWrapper?.dataset.videoTitle || 'YouTube video player';
      this.youtubeIframe = this.youtubePlayerMount?.querySelector('iframe') || null;
      this.youtubeReady = Boolean(this.youtubeIframe);
      this.youtubeLoadTimer = null;

      this.isMuted = this.defaultMuted;
      this.wasUnmuted = !this.defaultMuted;
      this.isPlaying = false;
      this.hasInteracted = false;
      this.visibilityObserver = null;

      this.handleSoundToggle = this.toggleSound.bind(this);
      this.handlePlayPauseToggle = (event) => {
        event.stopPropagation();
        this.togglePlayPause();
      };
      this.handleContainerClick = this.handleContainerClick.bind(this);
      this.handleDocumentVisibilityChange = this.handleDocumentVisibilityChange.bind(this);
    }

    init() {
      if (this.soundBtn) {
        this.soundBtn.addEventListener('click', this.handleSoundToggle);
      }

      if (this.playPauseBtn) {
        this.playPauseBtn.addEventListener('click', this.handlePlayPauseToggle);
      }

      this.container.addEventListener('click', this.handleContainerClick);
      document.addEventListener('visibilitychange', this.handleDocumentVisibilityChange);

      if (this.type === 'shopify' && this.video) {
        this.initShopifyVideo();
      } else if (this.type === 'youtube' && this.youtubeWrapper && this.youtubeVideoId) {
        this.initYouTubeVideo();
      } else if (this.type === 'vimeo') {
        this.initVimeoVideo();
      }

      this.updateSoundButton();

      if (this.type !== 'vimeo') {
        this.updatePlayPauseState(false);
      }
    }

    destroy() {
      this.pauseVideo({ forceMute: true });
      this.visibilityObserver?.disconnect?.();
      document.removeEventListener('visibilitychange', this.handleDocumentVisibilityChange);
      this.container.removeEventListener('click', this.handleContainerClick);
      this.soundBtn?.removeEventListener('click', this.handleSoundToggle);
      this.playPauseBtn?.removeEventListener('click', this.handlePlayPauseToggle);

      if (this.youtubeLoadTimer) {
        window.clearTimeout(this.youtubeLoadTimer);
      }

      this.container.removeAttribute('data-initialized');
      delete this.container._fullVideoInstance;
    }

    initShopifyVideo() {
      this.video.muted = this.isMuted;

      this.createVisibilityObserver((isVisible) => {
        if (isVisible) {
          if (this.video.preload === 'none') {
            this.video.preload = 'auto';
          }

          if (this.autoplay || this.hasInteracted) {
            this.playVideo();
          }
        } else {
          this.pauseVideo({ forceMute: true });
        }
      });

      this.visibilityObserver.observe(this.video);
    }

    initYouTubeVideo() {
      this.createVisibilityObserver((isVisible) => {
        if (isVisible) {
          if (this.autoplay || this.hasInteracted || this.youtubeIframe) {
            this.ensureYouTubePlayer({ autoplay: this.autoplay || this.hasInteracted });
            this.playVideo();
          }
        } else if (this.youtubeIframe) {
          this.pauseVideo({ forceMute: true });
        }
      });

      this.visibilityObserver.observe(this.youtubeWrapper);
    }

    initVimeoVideo() {
      this.updatePlayPauseState(this.autoplay);
    }

    createVisibilityObserver(callback) {
      if (!('IntersectionObserver' in window)) {
        this.visibilityObserver = {
          observe() {
            callback(true);
          },
          disconnect() {},
        };
        return;
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          callback(entry.isIntersecting);
        });
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      });
    }

    ensureYouTubePlayer({ autoplay = false } = {}) {
      if (this.youtubeIframe || !this.youtubePlayerMount || !this.youtubeVideoId) {
        return;
      }

      const params = new URLSearchParams({
        autoplay: autoplay ? '1' : '0',
        controls: '0',
        enablejsapi: '1',
        iv_load_policy: '3',
        modestbranding: '1',
        mute: this.isMuted ? '1' : '0',
        origin: window.location.origin,
        playsinline: '1',
        rel: '0',
      });

      if (this.loop) {
        params.set('loop', '1');
        params.set('playlist', this.youtubeVideoId);
      }

      const iframe = document.createElement('iframe');
      iframe.src = `${YOUTUBE_EMBED_ORIGIN}/embed/${encodeURIComponent(this.youtubeVideoId)}?${params.toString()}`;
      iframe.title = this.youtubeTitle;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';

      iframe.addEventListener('load', () => {
        this.youtubeReady = true;
        this.youtubeWrapper.classList.add(CLASSES.youtubeLoaded);

        this.youtubeLoadTimer = window.setTimeout(() => {
          this.syncYouTubeState();
        }, 150);
      }, { once: true });

      this.youtubePlayerMount.textContent = '';
      this.youtubePlayerMount.appendChild(iframe);
      this.youtubeIframe = iframe;
    }

    syncYouTubeState() {
      if (!this.youtubeIframe || !this.youtubeReady) return;

      if (this.wasUnmuted) {
        postYouTubeCommand(this.youtubeIframe, 'unMute');
        this.isMuted = false;
      } else {
        postYouTubeCommand(this.youtubeIframe, 'mute');
        this.isMuted = true;
      }

      if (this.isPlaying) {
        postYouTubeCommand(this.youtubeIframe, 'playVideo');
      } else {
        postYouTubeCommand(this.youtubeIframe, 'pauseVideo');
      }

      this.updateSoundButton();
    }

    handleContainerClick(event) {
      if (event.target.closest(SELECTORS.soundBtn) || event.target.closest(SELECTORS.playPauseBtn)) {
        return;
      }

      this.togglePlayPause();
    }

    handleDocumentVisibilityChange() {
      if (document.hidden) {
        this.pauseVideo({ forceMute: true });
        return;
      }

      if (!isElementInViewport(this.container)) {
        return;
      }

      if (this.autoplay || this.hasInteracted) {
        this.playVideo();
      }
    }

    playVideo() {
      if (this.type === 'shopify' && this.video) {
        if (this.video.preload === 'none') {
          this.video.preload = 'auto';
        }

        this.restorePreferredSound();

        const playPromise = this.video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            this.updatePlayPauseState(false);
          });
        }

        this.updatePlayPauseState(true);
        return;
      }

      if (this.type === 'youtube') {
        this.ensureYouTubePlayer({ autoplay: true });
        this.restorePreferredSound();
        this.updatePlayPauseState(true);

        if (!this.youtubeReady) {
          return;
        }

        postYouTubeCommand(this.youtubeIframe, 'playVideo');
      }
    }

    pauseVideo({ forceMute = false } = {}) {
      if (this.type === 'shopify' && this.video) {
        this.video.pause();

        if (forceMute) {
          this.muteForBackground();
        }

        this.updatePlayPauseState(false);
        return;
      }

      if (this.type === 'youtube' && this.youtubeIframe) {
        if (forceMute) {
          this.muteForBackground();
        }

        this.updatePlayPauseState(false);

        if (this.youtubeReady) {
          postYouTubeCommand(this.youtubeIframe, 'pauseVideo');
        }
      }
    }

    togglePlayPause() {
      this.hasInteracted = true;

      if (this.type === 'vimeo') {
        return;
      }

      if (this.isPlaying) {
        this.pauseVideo();
        return;
      }

      this.playVideo();
    }

    toggleSound(event) {
      event.stopPropagation();

      if (this.type !== 'shopify' && this.type !== 'youtube') {
        return;
      }

      const nextMutedState = !this.isMuted;
      this.setMuted(nextMutedState);

      if (this.type === 'youtube' && this.youtubeReady) {
        postYouTubeCommand(this.youtubeIframe, nextMutedState ? 'mute' : 'unMute');
      }
    }

    setMuted(isMuted, { preserveRestorePreference = false } = {}) {
      this.isMuted = isMuted;

      if (!preserveRestorePreference) {
        this.wasUnmuted = !isMuted;
      }

      if (this.type === 'shopify' && this.video) {
        this.video.muted = isMuted;
      }

      this.updateSoundButton();
    }

    muteForBackground() {
      this.wasUnmuted = !this.isMuted;
      this.setMuted(true, { preserveRestorePreference: true });

      if (this.type === 'youtube' && this.youtubeReady) {
        postYouTubeCommand(this.youtubeIframe, 'mute');
      }
    }

    restorePreferredSound() {
      this.setMuted(!this.wasUnmuted, { preserveRestorePreference: true });

      if (this.type === 'youtube' && this.youtubeReady) {
        postYouTubeCommand(this.youtubeIframe, this.wasUnmuted ? 'unMute' : 'mute');
      }
    }

    updateSoundButton() {
      if (!this.soundBtn) return;

      this.soundBtn.classList.toggle(CLASSES.unmuted, !this.isMuted);
      this.soundBtn.setAttribute('aria-label', this.isMuted ? 'Unmute video' : 'Mute video');
    }

    updatePlayPauseState(isPlaying) {
      this.isPlaying = isPlaying;
      this.container.classList.toggle(CLASSES.paused, !isPlaying);

      if (!this.playPauseBtn) return;

      this.playPauseBtn.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      this.playPauseBtn.setAttribute('aria-pressed', String(isPlaying));
    }
  }

  function initFullVideoSections(container = document) {
    const sections = container.matches?.(SELECTORS.section)
      ? [container]
      : container.querySelectorAll(SELECTORS.section);

    sections.forEach((section) => {
      const videoContainer = section.querySelector(SELECTORS.container);
      if (!videoContainer || videoContainer.dataset.initialized === 'true') {
        return;
      }

      const instance = new VideoSection(videoContainer);
      instance.init();
      videoContainer.dataset.initialized = 'true';
      videoContainer._fullVideoInstance = instance;
    });
  }

  if (!customElements.get('full-video-section')) {
    class FullVideoSection extends HTMLElement {
      connectedCallback() {
        this.visibilityObserver = whenVisible(this, () => initFullVideoSections(this));
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect?.();
        this.querySelector(SELECTORS.container)?._fullVideoInstance?.destroy();
      }
    }

    customElements.define('full-video-section', FullVideoSection);
  }
})();
